import cx from 'classnames'
import { useMemo } from 'react'

import Styles from '../SelectedPlanForm/SelectedPlanForm.module.scss'

import { AutopilotPlan, StripePrice } from 'components/Settings/Workspace/VirtualMarketerUpgradeCta/types'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { Pressable } from '@react-aria/interactions'
import { calculateExtraAccountCharges } from 'components/Settings/TieredBilling/utils'
import { useRootStore } from 'stores/RootStore'

export const PlanLengthSelector = ({
  selectedPlan,
  selectedPrice,
  onSelectPrice,
}: {
  selectedPlan: AutopilotPlan
  selectedPrice: StripePrice | null
  onSelectPrice: (price: StripePrice) => void
}) => {
  const { currentSubscription } = useRootStore().tierUsageStore

  const sortedPrices = useMemo(
    () => [...selectedPlan.prices].sort((a, b) => b.recurring.interval_count - a.recurring.interval_count),
    [selectedPlan.prices],
  )

  const monthlyBasePrice = useMemo(() => {
    const monthlyPlan = selectedPlan.prices.find((p) => p.recurring.interval_count === 1)
    return monthlyPlan ? monthlyPlan.unit_amount / 100 : null
  }, [selectedPlan.prices])

  return (
    <div className={Styles.container} data-vertical-plan-length="on">
      <div className={Styles.pricesContainer}>
        {sortedPrices.map((price) => {
          const extraAccountCharges = calculateExtraAccountCharges(currentSubscription, price.recurring.interval_count)
          const basePriceMonthly = Math.ceil(price.unit_amount / 100 / price.recurring?.interval_count)
          const extraAccountMonthly = Math.ceil(extraAccountCharges / price.recurring.interval_count)
          const totalMonthly = basePriceMonthly + extraAccountMonthly
          const totalPriceWihoutExtraAccountCharges = price.unit_amount / 100
          const originalTotal =
            monthlyBasePrice && price.recurring.interval_count > 1
              ? Math.round(monthlyBasePrice * price.recurring.interval_count)
              : null
          const isPopular = price.recurring.interval_count === 12

          return (
            <Pressable onPress={() => onSelectPrice(price)} key={price.id}>
              <div className={cx(Styles.price, { [Styles.selected]: price.id === selectedPrice?.id })}>
                <div className={Styles.priceLeft}>
                  <Heading level={3}>
                    {price.recurring.interval_count === 1
                      ? 'Monthly plan'
                      : `${price.recurring.interval_count} month plan`}
                  </Heading>
                  {isPopular && <span className={Styles.mostPopular}>Most popular</span>}
                </div>
                <div className={Styles.priceRight}>
                  <div className={Styles.priceTopRow}>
                    {price.metadata.percent_off && (
                      <span className={Styles.percentOffPill}>{price.metadata.percent_off}% off</span>
                    )}
                    <div className={Styles.priceAmount}>
                      <Heading level={4}>${totalMonthly}</Heading>
                      <Text variant="placeholder">/mo</Text>
                    </div>
                  </div>
                  {price.recurring.interval_count > 1 && (
                    <div className={Styles.totalBilledContainer}>
                      {originalTotal && originalTotal !== Math.round(totalPriceWihoutExtraAccountCharges) && (
                        <span className={Styles.originalTotal}>${originalTotal.toLocaleString()}</span>
                      )}
                      <Text variant="metadata" color="var(--dark-40)">
                        ${totalPriceWihoutExtraAccountCharges.toLocaleString()} total billed
                      </Text>
                    </div>
                  )}
                </div>
              </div>
            </Pressable>
          )
        })}
      </div>
    </div>
  )
}
